export interface InspiratieSubcategorie {
  titel: string;
  ideeen: string[];
}

export interface InspiratieOnderwerp {
  id: string;
  titel: string;
  intro: string;
  subcategorieen: InspiratieSubcategorie[];
}

export function countInspiratieIdeeen(onderwerp: InspiratieOnderwerp): number {
  return onderwerp.subcategorieen.reduce((sum, sub) => sum + sub.ideeen.length, 0);
}

export const INSPIRATIE_ONDERWERPEN: InspiratieOnderwerp[] = [
  {
    id: "verbaal",
    titel: "Verbaal",
    intro: "Taakideeën die primair op taal, woorden en categoriseren leunen.",
    subcategorieen: [
      {
        titel: "Woorden",
        ideeen: [
          "Woorden spellen",
          "Woorden achterstevoren spellen",
          "Trauma-gerelateerde woorden spellen",
          "Woorden alfabetisch opnoemen",
        ],
      },
      {
        titel: "Categorieën",
        ideeen: [
          "Dieren noemen",
          "Beroepen noemen",
          "Fruit noemen",
          "Dingen die rood zijn",
          "Dingen die je in een supermarkt vindt",
          "Dingen die kunnen vliegen",
          "Landen noemen",
          "Films noemen",
          "Steden noemen",
          "Liedjes noemen",
          "Merken noemen",
        ],
      },
      {
        titel: "Taalspelletjes",
        ideeen: [
          "Rijmwoorden bedenken",
          "Tegenstellingen noemen",
          "Woorden met dezelfde beginletter noemen",
          "Dieren van A t/m Z",
          "Zinnen afmaken",
          "Reeks woorden onthouden en herhalen",
        ],
      },
      {
        titel: "Originele vragen",
        ideeen: [
          "Noem zoveel mogelijk dingen die stinken",
          "Noem dingen die paars zijn",
          "Noem dieren met een lange nek",
          "Doe een robotstem terwijl je terugtelt",
          "Noem beroepen alsof je een piraat bent",
        ],
      },
    ],
  },
  {
    id: "auditief",
    titel: "Auditief",
    intro: "Taken met luisteren, nazeggen, ritme en geluid.",
    subcategorieen: [
      {
        titel: "Reeksen",
        ideeen: [
          "Cijfers nazeggen",
          "Cijfers achterstevoren nazeggen",
          "Woorden nazeggen",
          "Woorden achterstevoren nazeggen",
        ],
      },
      {
        titel: "Muziek en ritme",
        ideeen: ["Ritme klappen en laten herhalen", "Trommelen"],
      },
      {
        titel: "Geluiden",
        ideeen: [
          "Geluiden herkennen",
          "Aantal tikken tellen",
          "Hoge/lage tonen onderscheiden",
          "Liedje (mee)zingen",
        ],
      },
    ],
  },
  {
    id: "visueel",
    titel: "Visueel",
    intro: "Visuele taken: volgen, herkennen, sorteren en puzzels.",
    subcategorieen: [
      {
        titel: "Volgen",
        ideeen: [
          "EMDR-lamp volgen",
          "Knikker op een knikkerbaan volgen",
          "Raceauto volgen op een filmpje",
          "Elektrisch treintje of autootje volgen",
        ],
      },
      {
        titel: "Herkennen",
        ideeen: [
          "Kleuren benoemen op een werkblad",
          "Vormen benoemen op een werkblad",
          "Symbolen herkennen op een werkblad",
          "Letters zoeken op een werkblad",
        ],
      },
      {
        titel: "Sorteren",
        ideeen: [
          "Kleuren sorteren",
          "Vormen sorteren",
          "Objecten van groot naar klein sorteren",
          "Van weinig naar veel sorteren",
        ],
      },
      {
        titel: "Visuele puzzels",
        ideeen: [
          "Doolhof",
          "Zoekplaat",
          "Verschillen zoeken",
          "Tangram",
          "Sudoku",
          "Kruiswoordpuzzel",
          "Woordzoeker",
        ],
      },
    ],
  },
  {
    id: "motorisch",
    titel: "Motorisch",
    intro: "Beweging, balans, gooien en fijne motoriek.",
    subcategorieen: [
      {
        titel: "Groot motorisch",
        ideeen: [
          "Danspasjes",
          "Springen als een dier",
          "Boksen",
          "Touwtje springen",
          "Parcours volgen",
          "Laserlampje volgen",
        ],
      },
      {
        titel: "Klein motorisch",
        ideeen: [
          "Butterfly hug",
          "Verschillende ritmes tikken op eigen knieën",
          "Duim raakt om de beurt de andere vingers aan",
        ],
      },
      {
        titel: "Balans",
        ideeen: [
          "Op één been staan",
          "Balanceren op een bank",
          "Balanceren op een balance board",
        ],
      },
      {
        titel: "Gooien en vangen",
        ideeen: [
          "Bal overgooien",
          "Ballon hooghouden",
          "Blikken omgooien",
          "Doel raken met een bal",
        ],
      },
      {
        titel: "Fijne motoriek",
        ideeen: [
          "Kralen rijgen",
          "Pasta rijgen",
          "Knippen",
          "Stickers plakken",
          "Patronen natekenen",
        ],
      },
    ],
  },
  {
    id: "gecombineerd",
    titel: "Gecombineerd",
    intro: "Taken die meerdere zintuigen of vaardigheden combineren.",
    subcategorieen: [
      {
        titel: "Verbaal + motorisch",
        ideeen: [
          "Dieren noemen tijdens bal overgooien",
          "Terugtellen tijdens lopen",
          "Woorden spellen tijdens tapping",
          "Gerechten noemen tijdens boksen",
        ],
      },
      {
        titel: "Visueel + motorisch",
        ideeen: [
          "Lichtje volgen en aantikken",
          "Knikker op een baan volgen en tellen op vingers",
          "Kleuren sorteren onder tijdsdruk",
        ],
      },
      {
        titel: "Auditief + motorisch",
        ideeen: [
          "Ritme klappen en nadoen",
          "Muziek volgen en bewegen",
          "Op commando bewegen",
        ],
      },
      {
        titel: "Cognitief + motorisch",
        ideeen: [
          "Rekenen tijdens bal gooien",
          "Rekenreeks tijdens lopen",
          "Tafels opzeggen tijdens balanceren",
        ],
      },
    ],
  },
  {
    id: "speels-creatief",
    titel: "Speels / creatief",
    intro: "Fantasie, creatief materiaal, rollenspel, bouwen en spelletjes.",
    subcategorieen: [
      {
        titel: "Fantasie",
        ideeen: [
          "Bewegen als dieren",
          "Lopen als een robot of piraat",
          "Superheld spelen",
        ],
      },
      {
        titel: "Creatief materiaal",
        ideeen: [
          "Tekenen met scheerschuim",
          "Kleien",
          "Slijm maken",
          "Kinetisch zand",
        ],
      },
      {
        titel: "Rollenspel",
        ideeen: [
          "Sorteren of spelen met Playmobil",
          "Poppen gebruiken",
          "Handpoppen inzetten",
        ],
      },
      {
        titel: "Bouwactiviteiten",
        ideeen: [
          "Toren bouwen",
          "Domino-opstelling maken",
          "Constructies bouwen",
          "Huis bouwen met magnetic tiles",
          "Specifiek object bouwen met lego",
        ],
      },
      {
        titel: "Spelletjes",
        ideeen: ["Dobble", "Halli Galli", "Jenga", "Memory", "Mikado"],
      },
    ],
  },
  {
    id: "tactiel-sensorisch",
    titel: "Tactiel / sensorisch",
    intro: "Aanraking, proprioceptie en vibratie.",
    subcategorieen: [
      {
        titel: "Tactiel",
        ideeen: [
          "Trillers vasthouden",
          "Theratappers",
          "Stressbal knijpen",
          "Klei kneden",
          "Slijm voelen",
          "Kinetisch zand",
          "Kranten scheuren",
          "Butterfly hug",
        ],
      },
      {
        titel: "Proprioceptief",
        ideeen: [
          "Tegen een muur duwen",
          "Zware bal vasthouden",
          "Zware knuffel op schoot",
          "Trekspelletjes",
          "Duw- en trekactiviteiten",
        ],
      },
      {
        titel: "Vibratie",
        ideeen: [
          "Buzzers",
          "Trillers",
          "Massageroller",
          "Regen maken over armen/benen",
        ],
      },
    ],
  },
];
